import get from "lodash/get";
import { MomentInput } from "moment";
import { useCalculateTotalHours } from "PFApp/booking/hooks/use_calculate_total_hours";
import { isBookingWithTime } from "PFApp/booking/parts/helpers";
import { usePastDaysLimit } from "PFApp/hooks/use_past_days_limit";
import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import { utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { RequirementType } from "PFTypes";
import { RefObject, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingFormMode } from "../../booking_form.types";
import { BookingFormCommonInputs } from "../../booking_form_common_inputs";
import { BookingFormValues } from "../../booking_form_values.types";
import { BookingType } from "../../booking_type";
import { PhasesSelector } from "../../phases_selector";
import { RequirementSelector } from "../../requirement_selector";
import { calculateRepeatDays } from "./calculate_repeat_days";
import css from "./range_item.module.scss";
import { Repeats } from "./repeats";
import { TimeSelector } from "./time_selector";

type RangeItemProps = {
  itemIndex: number;
  portalRef: RefObject<HTMLDivElement | null>;
  mode: BookingFormMode;
};

export const RangeItem = ({ itemIndex, portalRef, mode }: RangeItemProps) => {
  const pastDaysLimit = usePastDaysLimit();
  const { formatISODate, formatISODateTime } = useDateFormatter();
  const { calculateTotalHours } = useCalculateTotalHours();
  const { t } = useTranslation();
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext<BookingFormValues>();

  const bookingType = useWatch<BookingFormValues>({ name: "bookingType" });

  const start = useWatch<BookingFormValues>({ name: `bookings.${itemIndex}.startDate` }) as string;
  const end = useWatch<BookingFormValues>({ name: `bookings.${itemIndex}.endDate` }) as string;
  const requirementType = useWatch<BookingFormValues>({ name: `bookings.${itemIndex}.requirement.type` });

  const isRepeated = bookingType === BookingType.Repeated;

  const [withTime, setWithTime] = useState(
    !!start && !!end && isBookingWithTime(utc(start), utc(String(end)))
  );

  const formatDate = (date: MomentInput) => {
    if (requirementType === RequirementType.Load) {
      return withTime ? formatISODateTime(date) : formatISODate(date);
    }
    return formatISODate(date);
  };

  const isTimeInDateAllowed = withTime && !isRepeated;
  const isNotCreateMode = mode !== BookingFormMode.Create;
  const isOverridesNonWorkingDaysDisabled = requirementType === RequirementType.Load || isRepeated;

  const endDateErrorKeys = [
    `bookings.${itemIndex}.endDateErrorAPI.submit`,
    `bookings.${itemIndex}.endDateErrorAPI.potential-warnings`
  ];
  const endDateErrorKey = endDateErrorKeys.find((key) => !!get(errors, key));
  const endDateError = endDateErrorKey ? get(errors, endDateErrorKey)?.message : undefined;
  const toggleWithTime = () => {
    setWithTime((prev) => !prev);
  };

  const hoursDiffBetweenStartEndWithoutTime = useMemo(
    () => calculateTotalHours(String(start), String(end)),
    [start, end, calculateTotalHours]
  );

  useEffect(() => {
    if (requirementType !== RequirementType.Load || !withTime || isRepeated) {
      setValue(`bookings.${itemIndex}.startDate`, formatISODate(utc(start)));
      setValue(`bookings.${itemIndex}.endDate`, formatISODate(utc(end)));
    }
  }, [requirementType, withTime, isRepeated]);

  return (
    <div className={css.wrapper}>
      <PhasesSelector
        phaseFormValueName={`bookings.${itemIndex}.phaseSourceId`}
        errorKeys={[
          `bookings.${itemIndex}.phaseSourceIdErrorAPI.submit`,
          `bookings.${itemIndex}.phaseSourceIdErrorAPI.potential-warnings`
        ]}
      />
      <div className={css.datesContainer}>
        <Controller
          name={`bookings.${itemIndex}.startDate`}
          control={control}
          render={({ field }) => (
            <InlineCalendar
              label={t(`${isTimeInDateAllowed ? "startDateAndTime" : "startDate"}`)}
              showTime={isTimeInDateAllowed}
              selectedDate={formatDate(utc(field.value))}
              shouldCloseOnSelection
              minDate={formatDate(
                utc()
                  .startOf("day")
                  .subtract(pastDaysLimit ?? 0, "days")
              )}
              handleChange={(date) => {
                field.onChange(date);
                const newEndDate = utc(String(end))?.isBefore(utc(date)) ? utc(date).endOf("day") : null;
                if (newEndDate) {
                  setValue(`bookings.${itemIndex}.endDate`, formatDate(newEndDate));
                }
                setValue(
                  `bookings.${itemIndex}.repeatDays`,
                  calculateRepeatDays({ start: utc(date), end: newEndDate ?? utc(String(end)) })
                );
              }}
              clearDate={formatDate(utc().startOf("day"))}
              calendarStyle={{ width: 350 }}
              portalRef={portalRef}
            />
          )}
        />
        <Controller
          name={`bookings.${itemIndex}.endDate`}
          control={control}
          render={({ field }) => (
            <InlineCalendar
              label={t(`${isTimeInDateAllowed ? "endDateAndTime" : "endDate"}`)}
              showTime={isTimeInDateAllowed}
              minDate={formatDate(utc(String(start)))}
              selectedDate={field.value}
              shouldCloseOnSelection
              handleChange={(date) => {
                field.onChange(date);
                const newStartDate = utc(String(start))?.isAfter(utc(date)) ? utc(date).startOf("day") : null;
                if (newStartDate) {
                  setValue(`bookings.${itemIndex}.startDate`, formatDate(newStartDate));
                }
                setValue(
                  `bookings.${itemIndex}.repeatDays`,
                  calculateRepeatDays({ start: newStartDate ?? utc(String(start)), end: utc(date) })
                );
              }}
              clearDate={formatDate(utc().endOf("day"))}
              calendarStyle={{ width: 350 }}
              portalRef={portalRef}
              error={endDateError}
            />
          )}
        />
      </div>
      {isRepeated && <TimeSelector itemIndex={itemIndex} portalRef={portalRef} />}
      {!isRepeated && (
        <RequirementSelector
          itemIndex={itemIndex}
          withTime={withTime}
          onWithTimeChange={toggleWithTime}
          startEndHoursDiff={hoursDiffBetweenStartEndWithoutTime}
        />
      )}
      {isRepeated && <Repeats itemIndex={itemIndex} />}
      {isNotCreateMode && (
        <BookingFormCommonInputs
          overridesDiaryTimeFormValueName={`bookings.${itemIndex}.overridesDiaryTime`}
          overridesNonWorkingTimeFormValueName={`bookings.${itemIndex}.overridesNonWorkingTime`}
          categoryFormValueName={`bookings.${itemIndex}.category`}
          categoryFormErrorKeys={[
            `bookings.${itemIndex}.category`,
            `bookings.${itemIndex}.categoryErrorAPI.submit`,
            `bookings.${itemIndex}.categoryErrorAPI.potential-warnings`
          ]}
          titleFormValueName={`bookings.${itemIndex}.title`}
          descriptionFormValueName={`bookings.${itemIndex}.description`}
          isOverridesNonWorkingDaysDisabled={isOverridesNonWorkingDaysDisabled}
        />
      )}
    </div>
  );
};
